import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  Alert,
  Container,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import axios from "axios";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import PaymentIcon from "@mui/icons-material/Payment";
import PrintIcon from "@mui/icons-material/Print";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import { useQuery } from "@tanstack/react-query";
import ReceiptPrint from "./ReceiptPrint";

const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const REFUND_STORAGE_KEY = "reception_refunds";

const TYPE_LABELS = {
  appointment: "OPD",
  prescription: "Prescription",
  laboratory: "Laboratory",
  patient_xray: "X-Ray",
  ultrasound: "Ultrasound",
  patient_echo: "Echo",
  endoscopy: "Endoscopy",
  endoscopy_record: "Endoscopy",
  ct_scan_record: "CT Scan",
  m_roy_record: "MRI",
  dental_treatment: "Dental",
  emergency: "Emergency",
  procedure: "Procedure",
  surgery: "Surgery",
  ipd_charge: "IPD",
  icu_charge: "ICU",
};

const getNetAmount = (item) => {
  const baseAmount = Number(item?.amount ?? item?.total_amount ?? 0);
  if (!Number.isFinite(baseAmount)) return null;
  // `amount` from dues is already the payable/net amount.
  return Math.max(0, baseAmount);
};

const getGrossAmount = (item) => {
  const net = Number(item?.amount ?? item?.total_amount ?? 0);
  const discount = Number(item?.discount ?? item?.discount_amount ?? 0);
  const safeNet = Number.isFinite(net) ? net : 0;
  const safeDiscount = Number.isFinite(discount) ? discount : 0;
  return Math.max(0, safeNet + safeDiscount);
};

const getRefundedAmountForItem = (item) => {
  try {
    const refunds = JSON.parse(localStorage.getItem(REFUND_STORAGE_KEY) || "[]");
    return refunds
      .filter((r) => String(r?.item_id) === String(item?.id) && String(r?.item_type) === String(item?.type))
      .reduce((sum, r) => sum + (Number(r?.amount) || 0), 0);
  } catch {
    return 0;
  }
};

export default function CollectByTicket() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [searchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("ticket");
  const ticketNumberFromUrl = searchParams.get("ticket_number");

  const [ticketInput, setTicketInput] = useState(ticketNumberFromUrl || ticketIdFromUrl || "");
  const [resolvedTicketId, setResolvedTicketId] = useState(ticketIdFromUrl ? Number(ticketIdFromUrl) : null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [searchingTickets, setSearchingTickets] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => ({}));
  const [collecting, setCollecting] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [collectDialogItem, setCollectDialogItem] = useState(null);
  const [discountInput, setDiscountInput] = useState("");
  const [refundDialogItem, setRefundDialogItem] = useState(null);
  const [refundAmountInput, setRefundAmountInput] = useState("");
  const [refundNoteInput, setRefundNoteInput] = useState("");
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();
  const searchDebounceRef = useRef(null);

  const headers = token ? { Authorization: `Token ${token}` } : {};

  const fetchTicketOptions = useCallback(
    async (query) => {
      const q = (query || ticketInput || "").trim();
      if (!q) {
        setTicketOptions([]);
        return;
      }
      setSearchingTickets(true);
      try {
        const url = `${baseUrl}/tickets/?search=${encodeURIComponent(q)}&page_size=20`;
        const res = await axios.get(url, { headers });
        const results = res.data?.results ?? res.data ?? [];
        const list = Array.isArray(results) ? results : [];
        setTicketOptions(list);
        if (list.length === 1) {
          setResolvedTicketId(list[0].id);
          setTicketInput(list[0].ticket_number || list[0].id.toString());
        } else if (list.length === 0) {
          toast.error(t("reception.ticketNotFound", "Ticket not found"));
        }
      } catch {
        setTicketOptions([]);
        toast.error(t("reception.loadError", "Failed to load tickets."));
      } finally {
        setSearchingTickets(false);
      }
    },
    [headers, t]
  );

  const handleTicketInputChange = (event, value, reason) => {
    setTicketInput(value || "");
    if (reason === "reset") return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = (value || "").trim();
    if (q.length < 1) {
      setTicketOptions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => fetchTicketOptions(q), 350);
  };

  const handleTicketOptionSelect = (event, option) => {
    if (option && option.id) {
      setResolvedTicketId(option.id);
      setTicketInput(option.ticket_number || option.id.toString());
      setTicketOptions([]);
    }
  };

  const duesQuery = useQuery({
    queryKey: ["ticket-dues", resolvedTicketId],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}/tickets/${resolvedTicketId}/dues/`, { headers });
      return res.data;
    },
    enabled: !!resolvedTicketId,
  });

  const ticket = duesQuery.data?.ticket;
  const items = duesQuery.data?.items ?? [];
  const unpaidItems = useMemo(() => items.filter((it) => !it.is_paid), [items]);
  const isLoadingDues = duesQuery.isLoading;
  const isErrorDues = duesQuery.isError;
  const duesError = duesQuery.error;

  const handleLoad = () => {
    const q = ticketInput.trim();
    if (!q) {
      toast.info(t("reception.enterTicketNumber", "Enter ticket number or ID"));
      return;
    }
    fetchTicketOptions(q);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const next = {};
      unpaidItems.forEach((it) => (next[`${it.type}-${it.id}`] = true));
      setSelectedIds(next);
    } else {
      setSelectedIds({});
    }
  };

  const handleSelectOne = (key, checked) => {
    setSelectedIds((prev) => ({ ...prev, [key]: checked }));
  };

  const selectedList = useMemo(
    () => unpaidItems.filter((it) => selectedIds[`${it.type}-${it.id}`]),
    [unpaidItems, selectedIds]
  );

  const collectOne = async (item) => {
    setCollectDialogItem(item);
    const existingDiscountRaw = Number(item?.discount ?? item?.discount_amount ?? 0);
    const existingDiscount = Number.isFinite(existingDiscountRaw) ? Math.max(0, existingDiscountRaw) : 0;
    setDiscountInput(String(existingDiscount));
  };

  const collectItems = async (list, options = {}) => {
    const { discountOverrideByKey = {} } = options;
    const toCollect = list.filter((it) => !it.is_paid);
    if (!toCollect.length) return;
    setCollecting(true);
    const errors = [];
    for (const item of toCollect) {
      try {
        const key = `${item.type}-${item.id}`;
        await collectSingle(item, discountOverrideByKey[key]);
      } catch (err) {
        errors.push({ item, err: err?.response?.data?.detail || err?.message });
      }
    }
    setCollecting(false);
    if (errors.length) {
      toast.error(
        t("reception.collectPartialError", "Some items could not be collected: ") +
          errors.map((e) => e.item.label).join(", ")
      );
    } else {
      toast.success(t("reception.collected", "Payment(s) recorded."));
    }
    duesQuery.refetch();
    setSelectedIds({});
  };

  const collectSingle = async (item, discountOverride) => {
    const { type, id, amount } = item;
    switch (type) {
      case "appointment":
        await axios.patch(
          `${baseUrl}/appointments/${id}/`,
          { status: 2, due: 0 },
          { headers }
        );
        break;
      case "prescription":
        await axios.patch(`${baseUrl}/prescriptions/${id}/`, { payment_status: 2 }, { headers });
        break;
      case "laboratory":
        await axios.patch(`${baseUrl}/laboratory-test-orders/${id}/`, { is_paid: true }, { headers });
        break;
      case "patient_xray":
        await axios.patch(`${baseUrl}/patient-xray/${id}/`, { is_paid: true }, { headers });
        break;
      case "ultrasound":
        await axios.patch(`${baseUrl}/ultrasounds/${id}/`, { is_paid: true }, { headers });
        break;
      case "patient_echo":
        await axios.patch(`${baseUrl}/patient-echo/${id}/`, { is_paid: true }, { headers });
        break;
      case "endoscopy":
      case "endoscopy_record":
        {
          const total = getGrossAmount(item);
          const rawExistingDiscount = Number(item?.discount ?? item?.discount_amount ?? 0);
          const existingDiscount = Number.isFinite(rawExistingDiscount) ? rawExistingDiscount : 0;
          const rawDiscount = Number.isFinite(Number(discountOverride)) ? Number(discountOverride) : existingDiscount;
          const discount = Math.min(Math.max(0, rawDiscount), total);
          const paid = Math.max(0, total - discount);
          await axios.patch(
            `${baseUrl}/endoscopy-records/${id}/`,
            {
              discount,
              amount: paid,
              is_paid: true,
            },
            { headers }
          );
        }
        break;
      case "ct_scan_record":
        {
          const total = getGrossAmount(item);
          const rawExistingDiscount = Number(item?.discount ?? item?.discount_amount ?? 0);
          const existingDiscount = Number.isFinite(rawExistingDiscount) ? rawExistingDiscount : 0;
          const rawDiscount = Number.isFinite(Number(discountOverride)) ? Number(discountOverride) : existingDiscount;
          const discount = Math.min(Math.max(0, rawDiscount), total);
          const paid = Math.max(0, total - discount);
          await axios.patch(
            `${baseUrl}/ct-scan-records/${id}/`,
            {
              discount,
              amount: paid,
              is_paid: true,
            },
            { headers }
          );
        }
        break;
      case "m_roy_record":
        {
          const total = getGrossAmount(item);
          const rawExistingDiscount = Number(item?.discount ?? item?.discount_amount ?? 0);
          const existingDiscount = Number.isFinite(rawExistingDiscount) ? rawExistingDiscount : 0;
          const rawDiscount = Number.isFinite(Number(discountOverride)) ? Number(discountOverride) : existingDiscount;
          const discount = Math.min(Math.max(0, rawDiscount), total);
          const paid = Math.max(0, total - discount);
          await axios.patch(
            `${baseUrl}/m-roy-records/${id}/`,
            {
              discount,
              amount: paid,
              is_paid: true,
            },
            { headers }
          );
        }
        break;
      case "dental_treatment":
        await axios.post(
          `${baseUrl}/dental-payments/`,
          { treatment: id, amount: amount || 0 },
          { headers: { ...headers, "Content-Type": "application/json" } }
        );
        break;
      case "emergency":
        await axios.post(`${baseUrl}/emergency-visits/${id}/mark-paid/`, {}, { headers });
        break;
      case "procedure":
        await axios.post(
          `${baseUrl}/procedure-payment/`,
          { procedure: id, amount: amount || 0 },
          { headers: { ...headers, "Content-Type": "application/json" } }
        );
        break;
      case "surgery":
        toast.info(t("reception.surgeryCollectNotSupported", "Surgery payment is not collected from this screen."));
        return;
      case "ipd_charge":
        await axios.post(`${baseUrl}/ipd-charges/${id}/mark-paid/`, {}, { headers });
        break;
      case "icu_charge":
        await axios.post(`${baseUrl}/icu-charges/${id}/mark-paid/`, {}, { headers });
        break;
      default:
        throw new Error(`Unknown type: ${type}`);
    }
  };

  const allUnpaidSelected = unpaidItems.length > 0 && unpaidItems.every((it) => selectedIds[`${it.type}-${it.id}`]);
  const someSelected = selectedList.length > 0;
  const collectDialogTotal = collectDialogItem ? getGrossAmount(collectDialogItem) : 0;
  const collectDialogDiscountRaw = Number(discountInput || 0);
  const collectDialogDiscount = Number.isFinite(collectDialogDiscountRaw) ? collectDialogDiscountRaw : 0;
  const collectDialogSafeDiscount = Math.min(Math.max(0, collectDialogDiscount), collectDialogTotal);
  const collectDialogPaid = Math.max(0, collectDialogTotal - collectDialogSafeDiscount);

  const handleConfirmCollectOne = async () => {
    if (!collectDialogItem) return;
    if (collectDialogSafeDiscount < 0 || collectDialogSafeDiscount > collectDialogTotal) {
      toast.error(t("reception.invalidDiscount", "Discount must be between 0 and total amount."));
      return;
    }
    const key = `${collectDialogItem.type}-${collectDialogItem.id}`;
    await collectItems([collectDialogItem], {
      discountOverrideByKey: { [key]: collectDialogSafeDiscount },
    });
    setCollectDialogItem(null);
    setDiscountInput("");
  };

  const handlePrintReceipt = (item) => {
    setPrintData({ ticket, item });
    setTimeout(() => {
      if (printRef.current) {
        const w = window.open("", "_blank");
        w.document.write(printRef.current.innerHTML);
        w.document.close();
        w.focus();
        w.print();
        w.close();
      }
    }, 300);
  };

  const openRefundDialog = (item) => {
    const netAmount = Number(getNetAmount(item) || 0);
    const alreadyRefunded = Number(getRefundedAmountForItem(item) || 0);
    const maxRefund = Math.max(0, netAmount - alreadyRefunded);
    setRefundDialogItem(item);
    setRefundAmountInput(maxRefund ? String(maxRefund) : "0");
    setRefundNoteInput("");
  };

  const handleConfirmRefund = () => {
    if (!refundDialogItem) return;
    const amount = Number(refundAmountInput || 0);
    const netAmount = Number(getNetAmount(refundDialogItem) || 0);
    const alreadyRefunded = Number(getRefundedAmountForItem(refundDialogItem) || 0);
    const maxRefund = Math.max(0, netAmount - alreadyRefunded);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(t("reception.invalidRefundAmount", "Enter a valid refund amount."));
      return;
    }
    if (amount > maxRefund) {
      toast.error(t("reception.refundTooHigh", "Refund cannot be more than paid amount."));
      return;
    }
    const existing = JSON.parse(localStorage.getItem(REFUND_STORAGE_KEY) || "[]");
    const refundRecord = {
      id: Date.now(),
      item_id: refundDialogItem.id,
      item_type: refundDialogItem.type,
      item_label: refundDialogItem.label,
      ticket_id: ticket?.id || null,
      ticket_number: ticket?.ticket_number || "",
      date: new Date().toISOString(),
      patient: {
        name: ticket?.patient_name || "",
      },
      patient_name: ticket?.patient_name || "",
      amount,
      note: refundNoteInput || "",
      created_at: new Date().toISOString(),
      currency: "AFN",
    };
    localStorage.setItem(REFUND_STORAGE_KEY, JSON.stringify([refundRecord, ...existing]));
    window.dispatchEvent(new Event("reception-refunds-updated"));
    toast.success(t("reception.refundSaved", "Refund saved."));
    setRefundDialogItem(null);
    setRefundAmountInput("");
    setRefundNoteInput("");
  };

  useEffect(() => {
    if (ticketIdFromUrl && /^\d+$/.test(ticketIdFromUrl)) {
      setResolvedTicketId(Number(ticketIdFromUrl));
    }
  }, [ticketIdFromUrl]);

  useEffect(() => {
    if (ticket && resolvedTicketId && ticket.ticket_number) {
      setTicketInput(ticket.ticket_number);
    }
  }, [resolvedTicketId, ticket?.ticket_number]);

  useEffect(() => {
    if (!ticketNumberFromUrl || resolvedTicketId != null) return;
    setTicketInput(ticketNumberFromUrl);
    const run = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/tickets/?search=${encodeURIComponent(ticketNumberFromUrl)}`,
          { headers }
        );
        const results = res.data?.results ?? res.data ?? [];
        const first = Array.isArray(results) ? results[0] : results;
        if (first?.id) setResolvedTicketId(first.id);
      } catch (_) {}
    };
    run();
  }, [ticketNumberFromUrl]);


  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("reception.paymentCollection", "Payment collection")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            inputValue={ticketInput}
            onInputChange={handleTicketInputChange}
            value={null}
            onChange={handleTicketOptionSelect}
            options={ticketOptions}
            getOptionLabel={(opt) => {
              const num = opt.ticket_number || opt.id?.toString() || "";
              const name = opt.patient_name ?? opt.patient_name_display ?? (opt.patient ? [opt.patient.firstname, opt.patient.lastname].filter(Boolean).join(" ") : null) ?? "";
              return name ? `${num} - ${name}` : num;
            }}
            loading={searchingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("reception.ticketNumberOrId", "Ticket number or ID")}
                placeholder={t("reception.ticketSearchPlaceholder", "e.g. 0001 or TKT-20260217-0001")}
                onKeyDown={(e) => e.key === "Enter" && (handleLoad(), e.preventDefault())}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchingTickets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleLoad} disabled={searchingTickets}>
            {t("common.load", "Load")}
          </Button>
        </Stack>

        {isErrorDues && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {duesError?.response?.data?.detail || duesError?.message || t("reception.loadError", "Failed to load dues.")}
          </Alert>
        )}

        {resolvedTicketId && ticket && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip label={ticket.ticket_number} color="primary" />
              <Typography variant="body2">{ticket.patient_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ticket.visit_date ? new Date(ticket.visit_date).toLocaleDateString() : ""}
              </Typography>
            </Stack>
          </Box>
        )}

        {isLoadingDues && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!resolvedTicketId && !isLoadingDues && (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("reception.enterTicketToSeeDues", "Enter ticket number to see all payments.")}
          </Typography>
        )}

        {resolvedTicketId && !isLoadingDues && !isErrorDues && items.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("reception.noChargeItems", "No charge items for this ticket.")}
          </Typography>
        )}

        {resolvedTicketId && !isLoadingDues && items.length > 0 && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={collecting ? <CircularProgress size={20} /> : <PaymentIcon />}
                disabled={!someSelected || collecting}
                onClick={() => collectItems(selectedList)}
              >
                {t("reception.collectSelected", "Collect selected")}
              </Button>
              <Button
                variant="outlined"
                disabled={collecting || unpaidItems.length === 0}
                onClick={() => {
                  if (window.confirm(t("reception.collectAllConfirm", "Collect payment for all unpaid items?"))) {
                    collectItems(unpaidItems);
                  }
                }}
              >
                {t("reception.collectAll", "Collect all")}
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {unpaidItems.length > 0 && (
                      <Checkbox
                        checked={allUnpaidSelected}
                        indeterminate={someSelected && !allUnpaidSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell><strong>{t("reception.type", "Type")}</strong></TableCell>
                  <TableCell><strong>{t("reception.description", "Description")}</strong></TableCell>
                  <TableCell><strong>{t("reception.referredBy", "Referred by")}</strong></TableCell>
                  <TableCell><strong>{t("reception.date", "Date")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("reception.discount", "Discount")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("reception.amount", "Amount")}</strong></TableCell>
                  <TableCell align="center"><strong>{t("reception.status", "Status")}</strong></TableCell>
                  <TableCell align="center"><strong>{t("reception.action", "Actions")}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const key = `${item.type}-${item.id}`;
                  const isPaid = !!item.is_paid;
                  const refunded = Number(getRefundedAmountForItem(item) || 0);
                  const isRefunded = refunded > 0;
                  const statusLabel = isRefunded
                    ? t("reception.refund", "Refund")
                    : isPaid
                    ? t("reception.paid")
                    : t("reception.pending");
                  const statusColor = isRefunded ? "info" : isPaid ? "success" : "warning";
                  return (
                    <TableRow key={key} hover sx={{ bgcolor: isPaid ? "action.hover" : undefined }}>
                      <TableCell padding="checkbox">
                        {!isPaid && (
                          <Checkbox
                            checked={!!selectedIds[key]}
                            onChange={(e) => handleSelectOne(key, e.target.checked)}
                          />
                        )}
                      </TableCell>
                      <TableCell>{item.type_display || TYPE_LABELS[item.type] || item.type}</TableCell>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>{item.doctor_display || "—"}</TableCell>
                      <TableCell>{item.date ? new Date(item.date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          const d = Number(item?.discount ?? item?.discount_amount ?? 0);
                          return Number.isFinite(d) ? `${d} ؋` : "—";
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const net = getNetAmount(item);
                          if (net == null) return "—";
                          const refunded = getRefundedAmountForItem(item);
                          return `${Math.max(0, net - refunded)} ؋`;
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusLabel}
                          color={statusColor}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Tooltip title={t("reception.viewDetails", "View details")}>
                            <IconButton size="small" onClick={() => setDetailsItem(item)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isPaid ? (
                            <>
                              <Tooltip title={t("reception.printReceipt", "Print receipt")}>
                                <IconButton size="small" color="primary" onClick={() => handlePrintReceipt(item)}>
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("reception.refund", "Refund")}>
                                <IconButton size="small" color="warning" onClick={() => openRefundDialog(item)}>
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={collecting}
                              onClick={() => collectOne(item)}
                            >
                              {t("reception.collect", "Collect")}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      {/* Details dialog */}
      <Dialog open={!!detailsItem} onClose={() => setDetailsItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("reception.itemDetails", "Item details")}</DialogTitle>
        <DialogContent>
          {detailsItem && (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Box><strong>{t("reception.type", "Type")}:</strong> {detailsItem.type_display || TYPE_LABELS[detailsItem.type] || detailsItem.type}</Box>
              <Box><strong>{t("reception.description", "Description")}:</strong> {detailsItem.label}</Box>
              <Box><strong>{t("reception.referredBy", "Referred by")}:</strong> {detailsItem.doctor_display || "—"}</Box>
              <Box><strong>{t("reception.date", "Date")}:</strong> {detailsItem.date ? new Date(detailsItem.date).toLocaleString() : "—"}</Box>
              <Box>
                <strong>{t("reception.discount", "Discount")}:</strong>{" "}
                {(() => {
                  const d = Number(detailsItem?.discount ?? detailsItem?.discount_amount ?? 0);
                  return Number.isFinite(d) ? `${d} ؋` : "—";
                })()}
              </Box>
              <Box><strong>{t("reception.amount", "Amount")}:</strong> {(() => { const net = getNetAmount(detailsItem); if (net == null) return "—"; const refunded = getRefundedAmountForItem(detailsItem); return `${Math.max(0, net - refunded)} ؋`; })()}</Box>
              <Box>
                <strong>{t("reception.status", "Status")}:</strong>{" "}
                {(() => {
                  const refunded = Number(getRefundedAmountForItem(detailsItem) || 0);
                  const isRefunded = refunded > 0;
                  const isPaid = !!detailsItem.is_paid;
                  const statusLabel = isRefunded
                    ? t("reception.refund", "Refund")
                    : isPaid
                    ? t("reception.paid")
                    : t("reception.pending");
                  const statusColor = isRefunded ? "info" : isPaid ? "success" : "warning";
                  return <Chip label={statusLabel} color={statusColor} size="small" />;
                })()}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden receipt for printing */}
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <ReceiptPrint data={printData} />
        </div>
      )}

      {/* Collect one dialog */}
      <Dialog
        open={!!collectDialogItem}
        onClose={() => {
          setCollectDialogItem(null);
          setDiscountInput("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("reception.collect", "Collect")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Box>
              <strong>{t("reception.totalAmount", "Total Amount")}:</strong>{" "}
              {collectDialogTotal.toFixed(2)} ؋
            </Box>
            <TextField
              size="small"
              label={t("reception.paidAmount", "Paid Amount")}
              value={collectDialogPaid.toFixed(2)}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label={t("reception.discount", "Discount")}
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
              inputProps={{ min: 0, max: collectDialogTotal, step: "0.01" }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCollectDialogItem(null);
              setDiscountInput("");
            }}
          >
            {t("reception.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCollectOne}
            disabled={collecting}
          >
            {t("reception.collect", "Collect")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!refundDialogItem}
        onClose={() => {
          setRefundDialogItem(null);
          setRefundAmountInput("");
          setRefundNoteInput("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("reception.refund", "Refund")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Box>
              <strong>{t("reception.item", "Item")}:</strong> {refundDialogItem?.label || "—"}
            </Box>
            <TextField
              size="small"
              type="number"
              label={t("reception.refundAmount", "Refund Amount")}
              value={refundAmountInput}
              onChange={(e) => setRefundAmountInput(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
            <TextField
              size="small"
              label={t("reception.note", "Note")}
              value={refundNoteInput}
              onChange={(e) => setRefundNoteInput(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRefundDialogItem(null);
              setRefundAmountInput("");
              setRefundNoteInput("");
            }}
          >
            {t("reception.cancel", "Cancel")}
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmRefund}>
            {t("reception.refund", "Refund")}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}
